import React, { useState, useEffect, useCallback, useRef, RefObject } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { XIcon, MessageSquareIcon, SparklesIcon, ArrowRightIcon, ListIcon } from '@components/common/ui/icons';
import * as styles from '@components/notes/NoteViewer.module.css';
import { useAuthStore } from '@hooks/useStores';
import { CommentService, Comment } from '@service/CommentService';
import { InlineCommentService, InlineComment } from '@service/InlineCommentService';
import {
    attachInlineCommentSync,
    applyLocalInlineCommentPermissions,
    normalizeCommentId,
    type InlineCommentWsEvent,
} from '@yjs/inline-comment-sync';
import { CommentItem } from './CommentItem';
import { InlineCommentItem } from './InlineCommentItem';
import { AiPanel } from './AiPanel';

type RightPanelTab = 'comments' | 'ai' | 'toc';
type CommentsSubTab = 'inline' | 'general';

interface PendingAnchor {
    yjsAnchor: string;
    anchorText: string;
}

interface EditorRightPanelProps {
    tab: RightPanelTab | null;
    noteId: string;
    onClose: () => void;
    onTabChange: (tab: RightPanelTab) => void;
    pendingAnchor: PendingAnchor | null;
    onClearAnchor: () => void;
    onScrollToAnchor: (yjsAnchor: string, anchorText?: string | null) => void;
    headings?: { level: number; text: string }[];
    onScrollToHeading?: (text: string) => void;
    scrollContainerRef?: RefObject<HTMLDivElement | null>;
    markdown?: string;
    selectedText?: string | null;
    wsProvider?: { ws: WebSocket | null; on: (event: string, handler: (arg: { status: string }) => void) => void; off?: (event: string, handler: (arg: { status: string }) => void) => void } | null;
    noteOwnerId?: string | null;
}

export const EditorRightPanel: React.FC<EditorRightPanelProps> = observer(({
    tab,
    noteId,
    onClose,
    onTabChange,
    pendingAnchor,
    onClearAnchor,
    onScrollToAnchor,
    headings = [],
    onScrollToHeading,
    scrollContainerRef,
    markdown = '',
    selectedText,
    wsProvider = null,
    noteOwnerId = null,
}) => {
    const { t } = useTranslation();
    const authStore = useAuthStore();
    const currentUserId = authStore.user?.id ?? null;

    const [subTab, setSubTab] = useState<CommentsSubTab>('inline');
    const [commentText, setCommentText] = useState('');
    const [sending, setSending] = useState(false);
    const inputRef = useRef<HTMLTextAreaElement>(null);

    const [activeHeading, setActiveHeading] = useState<string | null>(null);

    // IntersectionObserver — подсвечивает первый видимый заголовок в TOC
    useEffect(() => {
        if (tab !== 'toc' || !scrollContainerRef?.current) return;
        const root = scrollContainerRef.current;
        const headingEls = Array.from(
            root.querySelectorAll<HTMLElement>('h1,h2,h3,h4,h5,h6')
        );
        if (!headingEls.length) return;

        const observer = new IntersectionObserver(
            (entries) => {
                // Find topmost visible heading
                const visible = entries
                    .filter(e => e.isIntersecting)
                    .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
                if (visible.length > 0) {
                    setActiveHeading((visible[0].target as HTMLElement).innerText.trim());
                }
            },
            { root, threshold: 0, rootMargin: '0px 0px -80% 0px' }
        );

        headingEls.forEach(el => observer.observe(el));
        return () => observer.disconnect();
    }, [tab, scrollContainerRef, headings]);

    const [inlineComments, setInlineComments] = useState<InlineComment[]>([]);
    const [postComments, setPostComments]      = useState<Comment[]>([]);
    const [loading, setLoading]                = useState(false);

    const fetchComments = useCallback(async () => {
        if (!noteId) return;
        setLoading(true);
        try {
            const [inlineRes, postRes] = await Promise.all([
                InlineCommentService.getByNote(noteId),
                CommentService.getByNote(noteId),
            ]);
            setInlineComments(inlineRes.data);
            setPostComments(postRes.data);
        } catch {
            // ignore, panel will show empty state
        } finally {
            setLoading(false);
        }
    }, [noteId]);

    useEffect(() => {
        if (tab === 'comments') {
            fetchComments();
        }
    }, [tab, fetchComments]);

    const handleRemoteInlineEvent = useCallback(
        (event: InlineCommentWsEvent) => {
            if (event.action === 'deleted') {
                const deletedId = normalizeCommentId(event.commentId);
                setInlineComments((prev) => prev.filter((c) => c.id !== deletedId));
                return;
            }

            const remoteAuthorId =
                typeof event.comment.author === 'object' && event.comment.author?.id
                    ? String(event.comment.author.id)
                    : '';
            if (
                event.action === 'created' &&
                currentUserId &&
                remoteAuthorId === String(currentUserId)
            ) {
                return;
            }

            const normalized = applyLocalInlineCommentPermissions(
                event.comment,
                currentUserId,
                noteOwnerId,
            );

            setInlineComments((prev) => {
                const idx = prev.findIndex((c) => c.id === normalized.id);
                if (event.action === 'created') {
                    if (idx >= 0) return prev;
                    return [...prev, normalized];
                }
                if (idx < 0) return [...prev, normalized];
                const existing = prev[idx];
                return prev.map((c) =>
                    c.id === normalized.id
                        ? applyLocalInlineCommentPermissions(
                              normalized,
                              currentUserId,
                              noteOwnerId,
                              existing.myReaction,
                          )
                        : c,
                );
            });
        },
        [currentUserId, noteOwnerId],
    );

    useEffect(() => {
        return attachInlineCommentSync(noteId, wsProvider, handleRemoteInlineEvent);
    }, [noteId, wsProvider, handleRemoteInlineEvent]);

    // Когда появляется новый pending anchor — переключиться на Inline таб
    useEffect(() => {
        if (pendingAnchor) setSubTab('inline');
    }, [pendingAnchor]);

    // Autofocus input when panel opens or pending anchor arrives
    useEffect(() => {
        if (tab === 'comments') {
            setTimeout(() => inputRef.current?.focus(), 50);
        }
    }, [tab, pendingAnchor]);

    const handleSubmit = async () => {
        const text = commentText.trim();
        if (!text || sending) return;
        setSending(true);
        try {
            if (pendingAnchor) {
                // Inline comment
                const res = await InlineCommentService.create(noteId, {
                    content: text,
                    yjsAnchor: pendingAnchor.yjsAnchor,
                    anchorText: pendingAnchor.anchorText,
                });
                setInlineComments(prev => [...prev, res.data]);
                onClearAnchor();
            } else {
                // Post-level comment
                const res = await CommentService.create(noteId, text);
                setPostComments(prev => [...prev, res.data]);
                setSubTab('general');
            }
            setCommentText('');
        } catch {
            // keep text, let user retry
        } finally {
            setSending(false);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSubmit();
        }
        // Shift+Enter — default textarea behaviour (newline) — no override needed
    };

    // ── Inline comment handlers ─────────────────────────────────────
    const handleInlineDelete = async (id: string) => {
        await InlineCommentService.delete(id);
        setInlineComments(prev => prev.filter(c => c.id !== id));
    };

    const handleInlineReact = async (id: string, type: string) => {
        const res = await InlineCommentService.react(id, type);
        setInlineComments(prev => prev.map(c => c.id === id ? res.data : c));
    };

    const handleInlineResolve = async (id: string) => {
        const res = await InlineCommentService.resolve(id);
        setInlineComments(prev => prev.map(c => c.id === id ? res.data : c));
    };

    // ── Post-level comment handlers ─────────────────────────────────
    const handlePostDelete = async (id: string) => {
        await CommentService.delete(id);
        setPostComments(prev => prev.filter(c => c.id !== id));
    };

    const handlePostReact = async (id: string, type: string) => {
        const res = await CommentService.react(id, type);
        setPostComments(prev => prev.map(c => c.id === id ? res.data : c));
    };

    const activeInline  = inlineComments.filter(c => !c.isResolved);
    const resolvedCount = inlineComments.filter(c => c.isResolved).length;

    const inputPlaceholder = pendingAnchor
        ? t('editor.commentFor', {
            text: `${pendingAnchor.anchorText.slice(0, 30)}${pendingAnchor.anchorText.length > 30 ? '…' : ''}`,
        })
        : t('editor.addComment');

    return (
        <div
            className={styles.rightPanel}
            style={{ width: tab ? 280 : 0 }}
            aria-hidden={!tab}
        >
            {tab && (
                <div className={styles.rightPanelInner}>
                    {/* Header */}
                    <div className={styles.rightPanelHeader}>
                        <div className={styles.rightPanelTabs}>
                            <button
                                className={`${styles.rightPanelTab} ${tab === 'comments' ? styles.rightPanelTabActive : ''}`}
                                onClick={() => onTabChange('comments')}
                            >
                                {t('editor.comments')}
                            </button>
                            <button
                                className={`${styles.rightPanelTab} ${tab === 'ai' ? styles.rightPanelTabActive : ''}`}
                                onClick={() => onTabChange('ai')}
                            >
                                {t('editor.ai')}
                            </button>
                            <button
                                className={`${styles.rightPanelTab} ${tab === 'toc' ? styles.rightPanelTabActive : ''}`}
                                onClick={() => onTabChange('toc')}
                                title={t('editor.toc')}
                            >
                                <ListIcon className={styles.toolbarIcon} />
                            </button>
                        </div>
                        <button className={styles.rightPanelClose} onClick={onClose} title={t('editor.close')}>
                            <XIcon className={styles.toolbarIcon} />
                        </button>
                    </div>

                    {/* Comments tab */}
                    {tab === 'comments' && (
                        <div className={styles.rightPanelContent}>
                            {/* Sub-tabs: Inline / General */}
                            <div className={styles.rightPanelSubTabs}>
                                <button
                                    className={`${styles.rightPanelSubTab} ${subTab === 'inline' ? styles.rightPanelSubTabActive : ''}`}
                                    onClick={() => setSubTab('inline')}
                                >
                                    {t('editor.inline')} {activeInline.length > 0 && <span className={styles.badge}>{activeInline.length}</span>}
                                </button>
                                <button
                                    className={`${styles.rightPanelSubTab} ${subTab === 'general' ? styles.rightPanelSubTabActive : ''}`}
                                    onClick={() => setSubTab('general')}
                                >
                                    {t('editor.general')} {postComments.length > 0 && <span className={styles.badge}>{postComments.length}</span>}
                                </button>
                            </div>

                            {/* Pending anchor indicator */}
                            {pendingAnchor && (
                                <div className={styles.pendingAnchorBar}>
                                    <span>● «{pendingAnchor.anchorText.slice(0, 40)}»</span>
                                    <button onClick={onClearAnchor} title={t('editor.cancelAnchor')} className={styles.clearAnchorBtn}>
                                        <XIcon />
                                    </button>
                                </div>
                            )}

                            {/* Comment list */}
                            <div className={styles.commentList}>
                                {loading && (
                                    <div className={styles.rightPanelEmpty}>
                                        <p className={styles.rightPanelEmptyHint}>{t('editor.loading')}</p>
                                    </div>
                                )}

                                {!loading && subTab === 'inline' && activeInline.length === 0 && (
                                    <div className={styles.rightPanelEmpty}>
                                        <MessageSquareIcon className={styles.rightPanelEmptyIcon} />
                                        <p className={styles.rightPanelEmptyTitle}>{t('editor.noInlineComments')}</p>
                                        <p className={styles.rightPanelEmptyHint}>
                                            {t('editor.noInlineHint')}
                                        </p>
                                    </div>
                                )}

                                {!loading && subTab === 'general' && postComments.length === 0 && (
                                    <div className={styles.rightPanelEmpty}>
                                        <MessageSquareIcon className={styles.rightPanelEmptyIcon} />
                                        <p className={styles.rightPanelEmptyTitle}>{t('editor.noComments')}</p>
                                        <p className={styles.rightPanelEmptyHint}>{t('editor.writeFirstComment')}</p>
                                    </div>
                                )}

                                {!loading && subTab === 'inline' && activeInline.map(c => (
                                    <InlineCommentItem
                                        key={c.id}
                                        comment={c}
                                        onDelete={handleInlineDelete}
                                        onReact={handleInlineReact}
                                        onResolve={handleInlineResolve}
                                        onAnchorClick={(anchor) => onScrollToAnchor(anchor, c.anchorText)}
                                    />
                                ))}

                                {!loading && subTab === 'general' && postComments.map(c => (
                                    <CommentItem
                                        key={c.id}
                                        comment={c}
                                        onDelete={handlePostDelete}
                                        onReact={handlePostReact}
                                    />
                                ))}

                                {!loading && subTab === 'inline' && resolvedCount > 0 && (
                                    <p className={styles.resolvedHint}>
                                        {t('editor.resolvedHidden', { count: resolvedCount })}
                                    </p>
                                )}
                            </div>

                            {/* Input row */}
                            <div className={styles.rightPanelInputRow}>
                                <textarea
                                    ref={inputRef}
                                    className={styles.rightPanelInput}
                                    placeholder={inputPlaceholder}
                                    value={commentText}
                                    onChange={e => setCommentText(e.target.value)}
                                    onKeyDown={handleKeyDown}
                                    disabled={sending}
                                    rows={3}
                                />
                                <button
                                    className={styles.rightPanelSend}
                                    title={t('editor.send')}
                                    disabled={!commentText.trim() || sending}
                                    onClick={handleSubmit}
                                >
                                    <ArrowRightIcon className={styles.toolbarIcon} />
                                </button>
                            </div>
                        </div>
                    )}

                    {/* TOC tab */}
                    {tab === 'toc' && (
                        <div className={styles.rightPanelContent}>
                            {headings.length === 0 ? (
                                <div className={styles.rightPanelEmpty}>
                                    <ListIcon className={styles.rightPanelEmptyIcon} />
                                    <p className={styles.rightPanelEmptyTitle}>{t('editor.noHeadings')}</p>
                                    <p className={styles.rightPanelEmptyHint}>{t('editor.noHeadingsHint')}</p>
                                </div>
                            ) : (
                                <div className={styles.tocList}>
                                    {headings.map((h, i) => (
                                        <button
                                            key={i}
                                            className={`${styles.tocItem}${activeHeading === h.text ? ` ${styles.tocItemActive}` : ''}`}
                                            style={{ paddingLeft: `${(h.level - 1) * 12 + 12}px` }}
                                            onClick={() => onScrollToHeading?.(h.text)}
                                            title={h.text}
                                        >
                                            <span className={styles.tocItemLevel}>H{h.level}</span>
                                            <span className={styles.tocItemText}>{h.text}</span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* AI tab */}
                    {tab === 'ai' && (
                        <AiPanel markdown={markdown} selectedText={selectedText} />
                    )}
                </div>
            )}
        </div>
    );
});
